import { useErrorStore} from "@/stores/errorStore";
import type {  ProblemDetails } from '@/types/ProblemDetails'

/**
 * Kiota's fetch adapter throws these two when a 2xx response carries a body it cannot
 * deserialize (no content type at all, or a text body where structured data was expected).
 * The request itself succeeded — the import wizard's batch-save methods treat a match as
 * success rather than failing an import whose data actually landed.
 */
export function isBenignKiotaResponseParseError(error: any): boolean {
  const text = String(error ?? '');
  return text.includes('no response content type found')
    || text.includes('text does not support structured data');
}

export function handleGlobalError(error : any) {

  console.log('Global error handler triggered:', error);
  console.log('Error type:', typeof error);
  console.log('Error keys:', Object.keys(error));
  console.log('Error structure:', JSON.stringify(error, null, 2));

  if (error.handled) {
    return
  }

  // Kiota raises this when a response carries no body/content-type. It used to be discarded
  // silently, which made that failure mode completely invisible — a request could fail and leave
  // no trace anywhere. Still not surfaced to the user (it is usually a benign empty 204-ish
  // response), but it is at least logged now.
  if (error.toString().includes('no response content type found')) {
    console.warn('Suppressed Kiota no-content-type error (no response body to parse):', error);
    return
  }
  
  const errorStore = useErrorStore();

  // Handle Kiota ErrorResponse structure (errors in errors.additionalData without requiring additionalData.title)
  if (error.errors && error.errors.additionalData && typeof error.errors.additionalData === 'object') {
    console.log('Handling Kiota ErrorResponse with errors.additionalData');
    const errorItems = error.errors.additionalData;
    const parsedErrors = [];

    // Loop through numbered error keys (0, 1, 2, etc.)
    for (const key in errorItems) {
      const errorItem = errorItems[key];
      if (errorItem && (errorItem.name || errorItem.reason)) {
        parsedErrors.push({
          name: errorItem.name || 'Validation Error',
          reason: errorItem.reason || errorItem.message || 'An error occurred',
          code: error.statusCode?.toString() || error.responseStatusCode?.toString() || '400'
        });
      }
    }

    if (parsedErrors.length > 0) {
      const problemDetails: ProblemDetails = {
        status: error.statusCode || error.responseStatusCode || 400,
        title: error.messageEscaped || error.message || 'Validation Error',
        errors: parsedErrors
      };
      console.log('Created problemDetails from Kiota ErrorResponse:', problemDetails);
      errorStore.addError(problemDetails);
      return;
    }
  }

  // Handle specific Kiota error structure with nested additionalData and numbered error keys (legacy)
  if (error.additionalData && error.additionalData.title && error.errors && error.errors.additionalData) {
    console.log('Handling Kiota error structure with nested additionalData');
    const errorItems = error.errors.additionalData;
    const parsedErrors = [];
    
    // Loop through numbered error keys (0, 1, 2, etc.)
    for (const key in errorItems) {
      const errorItem = errorItems[key];
      if (errorItem && errorItem.name && errorItem.reason) {
        parsedErrors.push({
          name: errorItem.name,
          reason: errorItem.reason,
          code: error.additionalData.status?.toString() || '400'
        });
      }
    }
    
    if (parsedErrors.length > 0) {
      const problemDetails: ProblemDetails = {
        status: error.additionalData.status || 400,
        title: error.additionalData.title,
        errors: parsedErrors
      };
      console.log('Created problemDetails from Kiota nested structure:', problemDetails);
      errorStore.addError(problemDetails);
      return;
    }
  }
  
  // Handle case where error object itself contains the API response properties directly
  if (error.type && error.title && error.status && error.errors && Array.isArray(error.errors)) {
    console.log('Handling error object that matches API response structure directly');
    const problemDetails: ProblemDetails = {
      status: error.status,
      title: error.title,
      errors: error.errors.map((err: any) => ({
        name: err.name || 'Validation Error',
        reason: err.reason || err.message || 'An error occurred',
        code: err.code || error.status?.toString() || 'unknown'
      }))
    };
    console.log('Created problemDetails from direct error object:', problemDetails);
    errorStore.addError(problemDetails);
    return;
  }
  
  // Handle case where error has a data property with the response
  if (error.data && error.data.title && error.data.errors && Array.isArray(error.data.errors)) {
    console.log('Handling error.data structure');
    const problemDetails: ProblemDetails = {
      status: error.data.status || error.status || 400,
      title: error.data.title,
      errors: error.data.errors.map((err: any) => ({
        name: err.name || 'Validation Error',
        reason: err.reason || err.message || 'An error occurred',
        code: err.code || error.data.status?.toString() || 'unknown'
      }))
    };
    console.log('Created problemDetails from error.data:', problemDetails);
    errorStore.addError(problemDetails);
    return;
  }
  
  // Handle ProblemDetails response from API (direct error response)
  if (error.status && error.title && error.errors && Array.isArray(error.errors)) {
    console.log('Handling direct API error response');
    const problemDetails: ProblemDetails = {
      status: error.status,
      title: error.title,
      errors: error.errors.map((err: any) => ({
        name: err.name || 'Validation Error',
        reason: err.reason || err.message || 'An error occurred',
        code: err.code || error.status?.toString() || 'unknown'
      }))
    };
    console.log('Created problemDetails:', problemDetails);
    errorStore.addError(problemDetails);
    return;
  }
  
  // Handle wrapped error response (error.body contains the actual error)
  if(error.body !== undefined && error.body.title !== undefined) {
    console.log('Handling wrapped error response');
    const body = error.body;
    const problemDetails: ProblemDetails = {
      status: body.status || error.status || 500,
      title: body.title || 'An error occurred',
      errors: body.errors && Array.isArray(body.errors) ? 
        body.errors.map((err: any) => ({
          name: err.name || 'Error',
          reason: err.reason || err.message || 'An error occurred',
          code: err.code || body.status?.toString() || 'unknown'
        })) : 
        [{
          name: 'Error',
          reason: body.title || 'An error occurred',
          code: body.status?.toString() || 'unknown'
        }]
    };
    console.log('Created problemDetails from body:', problemDetails);
    errorStore.addError(problemDetails);
    return;
  }
  
  // Handle Kiota/Fetch API error responses that might have response data
  if (error.response && error.response.body) {
    console.log('Handling Kiota/Fetch API error with response body');
    try {
      const responseData = error.response.body;
      if (responseData.title && responseData.errors && Array.isArray(responseData.errors)) {
        const problemDetails: ProblemDetails = {
          status: responseData.status || error.response.status || 400,
          title: responseData.title,
          errors: responseData.errors.map((err: any) => ({
            name: err.name || 'Validation Error',
            reason: err.reason || err.message || 'An error occurred',
            code: err.code || responseData.status?.toString() || 'unknown'
          }))
        };
        console.log('Created problemDetails from response.body:', problemDetails);
        errorStore.addError(problemDetails);
        return;
      }
    } catch (parseError) {
      console.log('Error parsing response.body:', parseError);
    }
  }
  
  // Handle error that might be a JSON string
  if (typeof error === 'string') {
    console.log('Handling string error');
    try {
      const parsedError = JSON.parse(error);
      if (parsedError.title && parsedError.errors && Array.isArray(parsedError.errors)) {
        const problemDetails: ProblemDetails = {
          status: parsedError.status || 400,
          title: parsedError.title,
          errors: parsedError.errors.map((err: any) => ({
            name: err.name || 'Validation Error',
            reason: err.reason || err.message || 'An error occurred',
            code: err.code || parsedError.status?.toString() || 'unknown'
          }))
        };
        console.log('Created problemDetails from parsed string:', problemDetails);
        errorStore.addError(problemDetails);
        return;
      }
    } catch (parseError) {
      console.log('Error parsing JSON string:', parseError);
    }
  }
  
  if (error instanceof TypeError && error.message === 'Failed to fetch') {
    // This is a network error, which could be a connection refused error
    const err: ProblemDetails = {
      status: 500,
      title: 'Connection Refused',
      errors: [
        {
          name: 'Connection Refused',
          reason: 'The connection was refused when attempting to contact the requested resource.  We have been notified of the issue and are working to resolve it.  Please try again later.',
          code: '503'
        }
      ]
    };

    errorStore.addError(err);
    return;
  }
  
  // Handle legacy error structure with errors object
  if (error.errors && typeof error.errors === 'object' && !Array.isArray(error.errors)) {
    const reasons = Object.values(error.errors).map((err: any) => 
      typeof err === 'string' ? err : err.reason || err.message || err
    ).join(', ');
    const err: ProblemDetails = {
      status: error.status || 400,
      title: error.title || 'Validation Error',
      errors: [
        {
          name: 'Validation Error',
          reason: reasons,
          code: error.status?.toString() || '400'
        }
      ]
    };

    errorStore.addError(err);
    return;
  }
  
  // Handle generic error with message
  const reason = error.message || error.reason || 'An unexpected error occurred';
  const err: ProblemDetails = {
    status: error.status || 500,
    title: error.title || error.name || 'Error',
    errors: [
      {
        name: error.name || 'Error',
        reason: reason,
        code: error.status?.toString() || 'unknown'
      }
    ]
  };
  
  errorStore.addError(err); 


  
}
